import { useCallback, useEffect, useRef, useState } from "react";
import { getWebhookDeliveries } from "./api";
import { WebhookDelivery } from "./types";

export const useWebhookDeliveries = (
  endpointId: string | undefined,
  status: string,
  sport: string
) => {
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [nextCursor, setNextCursor] = useState<number>();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string>();
  const requestId = useRef(0);
  const inFlight = useRef<string>();
  const filterKey = JSON.stringify([endpointId, status, sport]);
  const currentFilter = useRef(filterKey);
  currentFilter.current = filterKey;

  const fetchDeliveries = useCallback(
    async (cursor?: number) => {
      if (
        !endpointId ||
        currentFilter.current !== filterKey ||
        (cursor !== undefined && inFlight.current === filterKey)
      )
        return;
      const request = ++requestId.current;
      inFlight.current = filterKey;
      const isCurrent = () =>
        request === requestId.current && currentFilter.current === filterKey;
      setError(undefined);
      setIsLoadingMore(cursor !== undefined);
      if (cursor === undefined) {
        setIsLoading(true);
        setDeliveries([]);
        setNextCursor(undefined);
      }
      try {
        const result = await getWebhookDeliveries(endpointId, {
          cursor,
          status: status || undefined,
          sport: sport || undefined,
        });
        if (!isCurrent()) return;
        setDeliveries((previous) =>
          cursor === undefined ? result.data : [...previous, ...result.data]
        );
        setNextCursor(result.meta?.next_cursor);
      } catch {
        if (isCurrent())
          setError("Unable to load deliveries. Please try again.");
      } finally {
        if (isCurrent()) {
          inFlight.current = undefined;
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [endpointId, status, sport, filterKey]
  );

  const invalidateRequest = useCallback(() => {
    ++requestId.current;
    inFlight.current = undefined;
  }, []);

  useEffect(() => {
    fetchDeliveries();
    return invalidateRequest;
  }, [fetchDeliveries, invalidateRequest]);

  return {
    deliveries,
    nextCursor,
    isLoading,
    isLoadingMore,
    error,
    fetchDeliveries,
  };
};
